import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useTriggerAutoSave } from "src/generator/hooks/useTriggerAutoSave";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setSectionProperty } from "src/redux/slices/generatorSlice";
import { insertValues } from "src/redux/utils/insertValues";
import { deepClone } from "src/utils";

interface Props {
  sectionIndex: number;
  rowIndex: number;
}
/**
 * Custom hook to toggle the strikethrough state of a row in a section.
 *
 * @param sectionIndex - The index of the section containing the row.
 * @param rowIndex - The index of the row to toggle the strikethrough state.
 * @returns A callback function to handle the strikethrough toggle.
 */
const useStrikethroughRow = ({ sectionIndex, rowIndex }: Props) => {
  const dispatch = useDispatch();
  const { getValues } = useFormContext();
  const triggetAutosave = useTriggerAutoSave();
  const { sections } = useSelector(s => s.generatorSlice.lbentry);

  return useCallback(() => {
    const section = deepClone(sections[sectionIndex]);
    const [sectionWithNewValues] = insertValues(getValues(), [section] as any);

    sectionWithNewValues.rows.forEach((row, index) => {
      if (index === rowIndex)
        Object.values(row.cols as any).forEach((col: any) => {
          col.strikethrough = !col.strikethrough;
        });
    });

    dispatch(
      setSectionProperty({
        sectionIndex,
        property: { rows: sectionWithNewValues.rows },
      }),
    );
    triggetAutosave();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
  }, [sectionIndex, rowIndex, sections, getValues]);
};
export default useStrikethroughRow;
